/* ─────────────────────────────────────────────────────────────
   Medicore collection page

   Three independent pieces:

     <load-more>       appends the next page in place of a pager
     the facets drawer opens the filter sidebar on mobile
     the description toggle expands a clamped collection description

   Everything degrades. Load more is a real <a href> to the next
   page, so without this script the buyer still gets pagination; the
   drawer's filters are a normal form; the description is clamped by
   CSS but its toggle stays hidden until the script confirms there is
   something to expand.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── load more ─────────────────────────────────────────────
     Custom element, so it re-initialises itself for free when the
     filter script replaces .result-container with a fresh page of
     results - a bound listener would not survive that swap.
     ─────────────────────────────────────────────────────────── */

  var LoadMore = class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.button = this.querySelector('[data-loadmore-btn]');
      if (!this.button) return;

      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      var trigger = event.target.closest('[data-loadmore-btn]');
      if (!trigger) return;

      // Modified clicks are the buyer asking for a new tab. Let the
      // href do its job.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();

      // Resolved at click time, not on connect: this element is
      // inserted with innerHTML alongside the grid, and reading the
      // document during that insertion is a race not worth running.
      this.grid = document.getElementById('collection-products');
      if (!this.grid) return;

      this.load();
    }

    load() {
      var self = this;
      var url = this.dataset.nextUrl;
      if (!url || this.classList.contains('is-loading')) return;

      this.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status + ' from ' + url);
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var incoming = doc.getElementById('collection-products');
          if (!incoming) throw new Error('no product grid in the next page');

          // The first appended card is where focus goes, so a keyboard
          // user carries on from the new products rather than being
          // dropped back at the top of the list.
          var firstNew = incoming.firstElementChild;

          while (incoming.firstElementChild) {
            self.grid.appendChild(incoming.firstElementChild);
          }

          self.sync(doc, firstNew);
        })
        .catch(function (error) {
          console.error('Load more failed:', error);
          // Fall back to the plain link rather than leaving a button
          // that silently does nothing.
          window.location.href = url;
        })
        .finally(function () {
          self.classList.remove('is-loading');
          self.button.removeAttribute('aria-busy');
        });
    }

    /* Reads the next page's own load-more element for the following
       URL, so the chain of pages is described by the pages
       themselves and never recomputed here. */
    sync(doc, firstNew) {
      var next = doc.querySelector('load-more');
      var nextUrl = next ? next.dataset.nextUrl : '';
      var loaded = this.grid.children.length;
      var total = parseInt(this.dataset.total, 10) || loaded;

      this.dataset.nextUrl = nextUrl || '';
      this.dataset.loaded = loaded;

      var bar = this.querySelector('[data-loadmore-bar]');
      if (bar) bar.style.width = Math.min(100, Math.round((loaded / total) * 100)) + '%';

      var loadedEl = this.querySelector('[data-loadmore-loaded]');
      if (loadedEl) loadedEl.textContent = loaded;

      // innerHTML rather than textContent: the template carries the
      // <strong> around each number. Both tokens are replaced with
      // integers this script computed, never with anything typed.
      var count = document.getElementById('collection-products-count');
      if (count && count.dataset.countTemplate) {
        var from = parseInt(count.dataset.from, 10) || 1;
        count.dataset.to = loaded;
        count.innerHTML = count.dataset.countTemplate
          .replace('[[from]]', '<strong>' + from + '</strong>')
          .replace('[[to]]', '<strong>' + loaded + '</strong>');
      }

      if (!nextUrl) {
        this.button.hidden = true;
        var end = this.querySelector('[data-loadmore-end]');
        if (end) end.hidden = false;
      } else {
        this.button.setAttribute('href', nextUrl);
      }

      // Appended cards need the theme's quick view bound to them.
      // main.js exports the binder for exactly this.
      if (typeof window.handleQuickView === 'function') window.handleQuickView();

      if (firstNew) {
        var focusTarget = firstNew.querySelector('a[href]');
        if (focusTarget) {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }
    }
  };

  if (!customElements.get('load-more')) {
    customElements.define('load-more', LoadMore);
  }

  /* ── sort ──────────────────────────────────────────────────
     Sorting is one query parameter, so it is set on the real URL and
     the result is swapped in - the same swap the filter script does,
     minus its habit of rebuilding the URL by string surgery on a
     data attribute.

     Delegated at the document because the select lives inside
     .result-container, which is replaced on every filter change.
     ─────────────────────────────────────────────────────────── */

  function swapResults(url) {
    var wrapper = document.querySelector('.load-wrapper');
    if (wrapper) wrapper.setAttribute('data-loading', 'loading');

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status + ' from ' + url);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');

        // The results, and the two filter regions that show what is
        // currently applied. The filter inputs themselves are left
        // alone: their checked state has not changed.
        ['.result-container', '.filter-head', '.active-filters'].forEach(function (selector) {
          var incoming = doc.querySelector(selector);
          var current = document.querySelector(selector);
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        });

        // The filter script builds every subsequent request from this
        // attribute, so the new sort has to land on it or the next
        // checkbox click would drop the sort.
        var form = document.querySelector('filter-form');
        var incomingForm = doc.querySelector('filter-form');
        if (form && incomingForm) form.dataset.url = incomingForm.dataset.url;

        window.history.replaceState({}, '', url);

        if (typeof window.handleQuickView === 'function') window.handleQuickView();
        syncFacetCount();
      })
      .catch(function (error) {
        console.error('Collection update failed:', error);
        window.location.href = url;
      })
      .finally(function () {
        if (wrapper) wrapper.removeAttribute('data-loading');
      });
  }

  /* ── custom sort control ───────────────────────────────────
     Built from the native select rather than rendered beside it, so
     the two can never drift apart and the page still sorts with the
     script switched off. Selecting an option writes the value back
     to the select and fires its change event - the handler below is
     the only thing that talks to the network.

     Implements the listbox pattern: the button owns aria-expanded
     and aria-activedescendant, the list owns role="listbox", and
     arrow keys move a selection rather than focus.
     ─────────────────────────────────────────────────────────── */

  function enhanceSort() {
    var root = document.querySelector('[data-mc-sort]');
    if (!root || root.dataset.sortEnhanced === 'true') return;

    var select = root.querySelector('select');
    var icons = root.querySelector('[data-mc-sort-icons]');
    if (!select || !select.options.length || !icons) return;

    root.dataset.sortEnhanced = 'true';

    var labelId = 'mc-sort-label';
    var label = root.querySelector('label');
    if (label) label.id = labelId;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'mc-sort__btn';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    var value = document.createElement('span');
    value.className = 'mc-sort__value';
    value.textContent = select.options[select.selectedIndex].textContent.trim();
    button.appendChild(value);
    button.appendChild(icons.content.querySelector('.mc-sort__chev').cloneNode(true));

    var menu = document.createElement('ul');
    menu.className = 'mc-sort__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('tabindex', '-1');
    if (label) menu.setAttribute('aria-labelledby', labelId);
    menu.hidden = true;

    var options = [];

    Array.prototype.forEach.call(select.options, function (option, index) {
      var item = document.createElement('li');
      item.className = 'mc-sort__opt';
      item.id = 'mc-sort-opt-' + index;
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;

      var text = document.createElement('span');
      text.textContent = option.textContent.trim();
      item.appendChild(text);
      item.appendChild(icons.content.querySelector('.mc-sort__check').cloneNode(true));

      if (option.selected) {
        item.classList.add('is-selected');
        item.setAttribute('aria-selected', 'true');
      } else {
        item.setAttribute('aria-selected', 'false');
      }

      menu.appendChild(item);
      options.push(item);
    });

    root.appendChild(button);
    root.appendChild(menu);
    root.classList.add('is-enhanced');

    var activeIndex = select.selectedIndex;

    function setActive(index) {
      if (index < 0 || index >= options.length) return;
      options.forEach(function (item) {
        item.classList.remove('is-active');
      });
      options[index].classList.add('is-active');
      activeIndex = index;
      button.setAttribute('aria-activedescendant', options[index].id);
    }

    function open() {
      if (!menu.hidden) return;
      menu.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      setActive(select.selectedIndex);
      menu.focus();
    }

    function close(focusButton) {
      if (menu.hidden) return;
      menu.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      button.removeAttribute('aria-activedescendant');
      if (focusButton) button.focus();
    }

    function choose(index) {
      var item = options[index];
      if (!item) return;

      close(true);
      if (select.value === item.dataset.value) return;

      select.value = item.dataset.value;
      value.textContent = item.firstChild.textContent;

      options.forEach(function (other) {
        var selected = other === item;
        other.classList.toggle('is-selected', selected);
        other.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    button.addEventListener('click', function () {
      if (menu.hidden) {
        open();
      } else {
        close(true);
      }
    });

    button.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
      }
    });

    menu.addEventListener('click', function (event) {
      var item = event.target.closest('.mc-sort__opt');
      if (item) choose(options.indexOf(item));
    });

    menu.addEventListener('mousemove', function (event) {
      var item = event.target.closest('.mc-sort__opt');
      if (item) setActive(options.indexOf(item));
    });

    menu.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActive((activeIndex + 1) % options.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          event.preventDefault();
          setActive(options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          choose(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          close(true);
          break;
        case 'Tab':
          close(false);
          break;
        default:
          break;
      }
    });

    // Losing focus to anywhere outside the control closes it. Checked
    // on the next frame because focus is briefly on nothing while it
    // moves between the button and the list.
    root.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!root.contains(document.activeElement)) close(false);
      }, 0);
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) close(false);
    });
  }

  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select.closest || !select.closest('[data-mc-sort]')) return;
    if (select.tagName !== 'SELECT') return;

    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    // Sorting a list means starting at its top again.
    url.searchParams.delete('page');

    swapResults(url.toString());
  });

  /* ── facets drawer ─────────────────────────────────────────
     Delegated at the document, because the toolbar button lives
     inside .result-container and is replaced wholesale every time a
     filter changes.
     ─────────────────────────────────────────────────────────── */

  var lastTrigger = null;

  function facets() {
    return document.querySelector('[data-facets]');
  }

  function openFacets(trigger) {
    var panel = facets();
    if (!panel) return;

    lastTrigger = trigger || null;
    panel.classList.add('is-open');
    document.body.classList.add('mc-facets-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var close = panel.querySelector('.mc-facets__close');
    if (close) close.focus();
  }

  function closeFacets() {
    var panel = facets();
    if (!panel || !panel.classList.contains('is-open')) return;

    panel.classList.remove('is-open');
    document.body.classList.remove('mc-facets-open');

    var trigger = document.querySelector('[data-facets-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  document.addEventListener('click', function (event) {
    var open = event.target.closest('[data-facets-open]');
    if (open) {
      event.preventDefault();
      openFacets(open);
      return;
    }

    if (event.target.closest('[data-facets-close]')) {
      event.preventDefault();
      closeFacets();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeFacets();
  });

  /* A filter can leave the viewport range where the drawer exists at
     all; leaving it open as a fixed panel on desktop would cover the
     grid. */
  window.addEventListener('resize', function () {
    if (window.innerWidth >= 1024) closeFacets();
  });

  /* The drawer's apply button carries the live result count. It sits
     outside .result-container, so it has to be told when the count
     inside changes. */
  function syncFacetCount() {
    var source = document.getElementById('collection-products-count');
    var target = document.querySelector('[data-facets-count]');
    if (!source || !target) return;

    var total = source.dataset.total;
    if (total) target.textContent = total;
  }

  var resultsWatched = false;

  function watchResults() {
    if (resultsWatched) return;

    var container = document.querySelector('.result-container');
    if (!container || !window.MutationObserver) return;

    resultsWatched = true;

    new MutationObserver(function () {
      syncFacetCount();
      // The toolbar, and with it the select, is replaced on every
      // filter and sort. Guarded by data-sort-enhanced, so the
      // mutations this itself causes are a no-op second time round.
      enhanceSort();
    }).observe(container, {
      childList: true,
      subtree: true
    });
  }

  /* ── description toggle ────────────────────────────────────
     The button only appears when the text is actually taller than
     its clamp - otherwise a two-line description gets a "Read more"
     that does nothing.
     ─────────────────────────────────────────────────────────── */

  function initDescription() {
    var wrap = document.querySelector('[data-coll-desc]');
    if (!wrap || wrap.dataset.collDescBound === 'true') return;
    wrap.dataset.collDescBound = 'true';

    var inner = wrap.querySelector('[data-coll-desc-inner]');
    var toggle = wrap.querySelector('[data-coll-desc-toggle]');
    if (!inner || !toggle) return;

    if (inner.scrollHeight - inner.clientHeight < 4) return;

    toggle.hidden = false;
    toggle.addEventListener('click', function () {
      var open = wrap.classList.toggle('is-open');
      toggle.querySelector('[data-coll-desc-more]').hidden = open;
      toggle.querySelector('[data-coll-desc-less]').hidden = !open;
    });
  }

  /* ── sticky offset ─────────────────────────────────────────
     Where the toolbar should pin. Measured from the header's live
     position rather than from its height, because `position: sticky`
     does not mean a header actually stays put: this one is wrapped in
     a div exactly its own height, so it scrolls away like a static
     header despite the declaration. Reading the computed style alone
     offset the toolbar by a header that was no longer on screen, and
     it pinned halfway down the page.

     bottom <= 0 means the header has left the viewport, so the
     toolbar takes the top edge. A header that genuinely stays pinned
     keeps its bottom edge positive and the toolbar sits under it.
     ─────────────────────────────────────────────────────────── */

  var stickyFrame = null;

  function syncStickyOffset() {
    stickyFrame = null;

    var header = document.querySelector('.mc-header, .ic-header');
    var offset = 0;

    if (header) {
      var position = window.getComputedStyle(header).position;
      if (position === 'sticky' || position === 'fixed') {
        offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      }
    }

    document.documentElement.style.setProperty('--mc-toolbar-top', offset + 'px');
  }

  function queueStickyOffset() {
    if (stickyFrame) return;
    stickyFrame = window.requestAnimationFrame(syncStickyOffset);
  }

  window.addEventListener('scroll', queueStickyOffset, { passive: true });
  window.addEventListener('resize', queueStickyOffset);
  window.addEventListener('load', syncStickyOffset);

  function init() {
    initDescription();
    enhanceSort();
    watchResults();
    syncFacetCount();
    syncStickyOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
